import type { FilterOption, FilterConfig } from './dto.inventory';
import type { ParsedFilterOptions } from './transformers.inventory';
import { isMy27Enabled } from '../../../../config/featureFlags';

/**
 * SAP codes shown individually in the Packages filter (one checkbox per code).
 * All other package codes (LUCID_PACKAGES / CCP*) are combined into one option.
 */
const SPECIFIC_PACKAGE_CODES = new Set([
  'DHP00',
  'DHP01',
  'TNP00',
  'TNP01',
  'TNP02',
  'TP00',
  'TP01',
]);

/** The single LUCID_PACKAGES code that represents the Gravity vehicle package. */
const GRAVITY_VEHICLE_PACKAGE_CODE = 'CCP05';

/** The comma-joined LUCID_PACKAGES codes that represent the Air vehicle package. */
const AIR_VEHICLE_PACKAGE_VALUE = 'CCP01,CCP02,CCP03';

/** The comma-joined LUCID_PACKAGES codes for Air vehicles WITHOUT the Comfort & Convenience package. */
const AIR_WITHOUT_PACKAGE_VALUE = 'CCP00,CCP04';

/** The comma-joined codes sent when the Towing Package option (TP01) is selected. */
const TOWING_PACKAGE_VALUE = 'TP01,TP02';

/** Label shown for the Air "without C&C" option. */
const AIR_WITHOUT_PACKAGE_LABEL = 'Without Comfort and Convenience Package';

/** Finds the most descriptive label for the combined Comfort & Convenience option. */
const findComfortLabel = (packages: FilterOption[]): string => {
  const found = packages.find(({ label }) => {
    const l = label.toLowerCase();
    return l.includes('comfort') || l.includes('convenience');
  });
  return found?.label ?? packages[0]?.label ?? '';
};

/**
 * Build package filter options:
 * - DHP00, DHP01, TNP00, TNP01, TNP02, TP00 shown individually
 * - TP01 (Towing Package) sends both TP01 and TP02 (e.g. "TP01,TP02")
 * - LUCID_PACKAGES combined into one "Comfort & Convenience" option:
 *   - Gravity: only CCP05
 *   - Air: all CCP codes comma-joined (e.g. "CCP01,CCP02,CCP03")
 */
export const buildPackageFilterOptions = (
  prCodes: ParsedFilterOptions | undefined,
  isGravity?: boolean,
  withoutPackageLabel?: string
): FilterOption[] => {
  const options: FilterOption[] = [];

  for (const opt of [
    ...(prCodes?.dynamicHandling ?? []),
    ...(prCodes?.technology ?? []),
    ...(prCodes?.towing ?? []),
  ]) {
    if (opt.value === 'TP01') {
      options.push({ ...opt, value: TOWING_PACKAGE_VALUE });
    } else if (SPECIFIC_PACKAGE_CODES.has(opt.value)) {
      options.push(opt);
    }
  }

  const allPackages = prCodes?.packages ?? [];
  if (allPackages.length > 0) {
    const value = isGravity ? GRAVITY_VEHICLE_PACKAGE_CODE : AIR_VEHICLE_PACKAGE_VALUE;
    options.push({
      value,
      label: findComfortLabel(allPackages),
    });
  }

  if (!isGravity && allPackages.length > 0) {
    options.push({
      value: AIR_WITHOUT_PACKAGE_VALUE,
      label: withoutPackageLabel ?? AIR_WITHOUT_PACKAGE_LABEL,
    });
  }

  return options;
};

export const buildPackageFilterGroups = (
  prCodes: ParsedFilterOptions | undefined,
  isGravity: boolean,
  withoutPackageLabel: string | undefined,
  groupLabels: { comfort: string; handling: string; technology: string; towing: string }
) => {
  const options = buildPackageFilterOptions(prCodes, isGravity, withoutPackageLabel);
  return [
    { key: 'packageComfort', prefix: 'CCP', title: groupLabels.comfort },
    { key: 'packageHandling', prefix: 'DHP', title: groupLabels.handling },
    { key: 'packageTechnology', prefix: 'TNP', title: groupLabels.technology },
    { key: 'packageTowing', prefix: 'TP', title: groupLabels.towing },
  ]
    .map(({ key, prefix, title }) => ({
      key,
      title,
      options: options.filter((option) => option.value.startsWith(prefix)),
    }))
    .filter((group) => group.options.length > 0);
};

/*
|--------------------------------------------------------------------------
| STATIC FILTER OPTIONS
| These options are not fetched from the API and remain constant
|--------------------------------------------------------------------------
*/

export const CONDITION_OPTIONS: FilterOption[] = [
  { label: 'New', value: 'new' },
  { label: 'Pre-owned', value: 'pre-owned' },
];

export const SEARCH_WITHIN_OPTIONS: FilterOption[] = [
  { label: 'Any miles', value: '10000' },
  { label: '500 miles', value: '500' },
  { label: '250 miles', value: '250' },
  { label: '100 miles', value: '100' },
  { label: '50 miles', value: '50' },
];

export const AVAILABILITY_OPTIONS: FilterOption[] = [
  { label: 'Available now', value: 'AVAILABLE_NOW' },
  { label: 'Available in 2 weeks', value: 'AVAILABLE_IN_2_WEEKS' },
  { label: 'Available in 3 weeks', value: 'AVAILABLE_IN_3_WEEKS' },
  { label: 'Available in 4 weeks', value: 'AVAILABLE_IN_4_WEEKS' },
  { label: 'Available in 8 weeks', value: 'AVAILABLE_IN_8_WEEKS' },
  { label: 'Available in 9 weeks', value: 'AVAILABLE_IN_9_WEEKS' },
];

export const AVAILABILITY_LABEL_KEYS: Record<string, string> = {
  AVAILABLE_NOW: 'filter.availability.availableNow',
  AVAILABLE_IN_2_WEEKS: 'filter.availability.availableIn2Weeks',
  AVAILABLE_IN_3_WEEKS: 'filter.availability.availableIn3Weeks',
  AVAILABLE_IN_4_WEEKS: 'filter.availability.availableIn4Weeks',
  AVAILABLE_IN_8_WEEKS: 'filter.availability.availableIn8Weeks',
  AVAILABLE_IN_9_WEEKS: 'filter.availability.availableIn9Weeks',
};

export const AIR_MODEL_YEAR_OPTIONS: FilterOption[] = [
  { label: '2027', value: '2027' },
  { label: '2026', value: '2026' },
  { label: '2025', value: '2025' },
  { label: '2023', value: '2023' },
];

export const GRAVITY_MODEL_YEAR_OPTIONS: FilterOption[] = [
  { label: '2026', value: '2026' },
  { label: '2027', value: '2027' },
];

export const AIR_TRIM_OPTIONS: FilterOption[] = [
  { label: 'Pure', value: 'PURE' },
  { label: 'Touring', value: 'AT' },
  { label: 'Grand Touring', value: 'GT' },
];

export const GRAVITY_TRIM_OPTIONS: FilterOption[] = [
  { label: 'Touring', value: 'AT' },
  { label: 'Touring Plus', value: 'TP' },
  { label: 'Grand Touring', value: 'GT' },
  { label: 'Grand Touring Ultimate', value: 'TU' },
];

/*
|--------------------------------------------------------------------------
| HELPER FUNCTIONS
|--------------------------------------------------------------------------
*/

/** Model year that unlocks the Gravity Touring Plus / Touring Ultimate trims. */
export const TOURING_TRIMS_MODEL_YEAR = '2027';

/** Gravity trims that are only offered for the {@link TOURING_TRIMS_MODEL_YEAR} model year. */
export const GRAVITY_2027_ONLY_TRIMS = ['TP', 'TU'];

/**
 * Get trim options based on selected model, using prCodes for translated labels
 *
 * For Gravity, the Touring Plus (TP) and Touring Ultimate (TU) trims are only
 * available when the 2027 model year is selected. For all other model years
 * (or when no year is selected) only Touring (AT) and Grand Touring (GT) are shown.
 *
 * @param isGravity - Whether Gravity model is selected
 * @param prCodes - Optional prCodes data for translated labels
 * @param selectedModelYears - Currently selected model year values (e.g. ['2027'])
 * @returns Appropriate trim options for the model
 */
export const getTrimOptionsForModel = (
  isGravity: boolean,
  prCodes?: ParsedFilterOptions,
  selectedModelYears?: string[]
): FilterOption[] => {
  const includeTouringTrims =
    (selectedModelYears?.includes(TOURING_TRIMS_MODEL_YEAR) ?? false) && isMy27Enabled();
  const gravityTrims = includeTouringTrims ? ['AT', 'TP', 'GT', 'TU'] : ['AT', 'GT'];
  const validTrims = isGravity ? gravityTrims : ['PURE', 'AT', 'GT'];
  const allModelOptions = isGravity ? GRAVITY_TRIM_OPTIONS : AIR_TRIM_OPTIONS;
  const fallbackOptions = allModelOptions.filter((opt) => validTrims.includes(opt.value));

  if (!prCodes?.modelVariant?.length) {
    return fallbackOptions;
  }

  // Use prCodes labels for matching trims, maintaining order
  return validTrims
    .map((trimValue) => {
      const prCodeOption = prCodes.modelVariant.find((opt) => opt.value === trimValue);
      const fallback = allModelOptions.find((opt) => opt.value === trimValue);
      return prCodeOption || fallback;
    })
    .filter((opt): opt is FilterOption => opt !== undefined);
};

/**
 * Get default trim values based on selected model
 * @param isGravity - Whether Gravity model is selected
 * @param selectedModelYears - Currently selected model year values (e.g. ['2027'])
 * @returns Default selected trim values for the model
 */
export const getDefaultTrimsForModel = (
  isGravity: boolean,
  selectedModelYears?: string[]
): string[] => {
  if (isGravity) {
    const include2027Trims =
      (selectedModelYears?.includes(TOURING_TRIMS_MODEL_YEAR) ?? false) && isMy27Enabled();
    return include2027Trims ? ['AT', 'TP', 'GT', 'TU'] : ['AT', 'GT'];
  }
  return ['PURE', 'AT', 'GT'];
};

/**
 * Build dynamic filter config by combining static options with prCodes data
 * @param prCodes - Parsed prCodes data from API
 * @param isGravity - Whether Gravity model is selected
 * @param selectedModelYears - Currently selected model year values (e.g. ['2027'])
 * @returns Complete filter configuration
 */
export const buildFilterConfig = (
  prCodes: ParsedFilterOptions | undefined,
  isGravity: boolean,
  selectedModelYears?: string[]
): FilterConfig => ({
  condition: CONDITION_OPTIONS,
  trim: getTrimOptionsForModel(isGravity, prCodes, selectedModelYears),
  searchWithin: SEARCH_WITHIN_OPTIONS,
  availability: AVAILABILITY_OPTIONS,
  exteriorColor: prCodes?.exteriorColor || [],
  interiorTheme: prCodes?.interiorTheme || [],
  wheels: prCodes?.wheels || [],
  appearance: prCodes?.appearance || [],
  driverAssistance: prCodes?.driverAssistance || [],
  soundSystem: prCodes?.soundSystem || [],
  seating: prCodes?.seating || [],
  packages: buildPackageFilterOptions(prCodes, isGravity),
});
